import { TZDate, tz } from '@date-fns/tz';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDay } from '@/shared/lib/format';
import { addDaysToDay, dayParts, marketToday, zoneName } from '@/shared/lib/time-zone';
import { cn } from '@/shared/lib/utils';

export interface DateRange {
  /** Market calendar day, yyyy-MM-dd; the server reads it in the market's zone. */
  from: string;
  to: string;
}

const PRESETS: ReadonlyArray<{ label: string; days: number }> = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

/** The last `days` market days, today included: exactly `days` days. */
export function lastNDays(days: number, zone: string, now: Date = new Date()): DateRange {
  const today = marketToday(zone, now);
  return { from: addDaysToDay(today, -(days - 1)), to: today };
}

/** A yyyy-MM-dd day as the calendar's date in the zone (noon, clear of any clock change). */
function toCalendarDate(value: string, zone: string): Date | undefined {
  const parts = dayParts(value);
  return parts ? TZDate.tz(zone, parts.year, parts.month - 1, parts.day, 12) : undefined;
}

/**
 * Date range filter, in market days.
 *
 * Presets first, because "last 30 days" covers the overwhelming majority of
 * operational queries and picking two dates by hand for it is needless work.
 * The calendar is there for the genuine "what happened on the 3rd" case (§44).
 * "Today" and the calendar are the market's (`timeZone`), never the browser's.
 *
 * The range is required rather than optional: it is the only filter the Orders
 * endpoint applies server-side, so an empty range would mean asking for every
 * order ever placed. See `orders.api.ts`.
 */
export function DateRangePicker({
  value,
  onChange,
  timeZone,
  className,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** The market zone the days belong to. */
  timeZone: string;
  className?: string;
}) {
  const fromDate = toCalendarDate(value.from, timeZone);
  const toDate = toCalendarDate(value.to, timeZone);

  const label =
    fromDate && toDate
      ? `${formatDay(value.from, 'd MMM')} – ${formatDay(value.to)}`
      : 'Select dates';
  const toDay = (date: Date) => format(date, 'yyyy-MM-dd', { in: tz(timeZone) });

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'border-input text-foreground hover:border-input hover:text-foreground h-8 justify-start gap-2 text-xs font-normal',
                className,
              )}
            >
              <CalendarIcon className="size-3.5" aria-hidden />
              {label}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Days in {zoneName(timeZone)}</TooltipContent>
      </Tooltip>

      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col gap-1 p-2">
          {PRESETS.map((preset) => (
            <Button
              key={preset.days}
              variant="ghost"
              size="sm"
              className="h-7 justify-start text-xs"
              onClick={() => onChange(lastNDays(preset.days, timeZone))}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <Separator />

        <Calendar
          mode="range"
          timeZone={timeZone}
          defaultMonth={fromDate}
          selected={{ from: fromDate, to: toDate }}
          onSelect={(range) => {
            // react-day-picker reports a partial range mid-selection; only
            // commit once both ends exist, or the table would refetch against
            // a half-chosen range.
            if (range?.from && range.to) {
              onChange({ from: toDay(range.from), to: toDay(range.to) });
            }
          }}
          numberOfMonths={1}
        />
      </PopoverContent>
    </Popover>
  );
}
